import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { projectService } from '@/lib/projectService';
import { sessionCache } from '@/lib/sessionCache';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Loader2,
  UserPlus,
  DollarSign,
  Target,
  Receipt,
  Users,
  Handshake,
  Building2,
  Ticket,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface CrmHomeProps {
  isGlobalDarkMode: boolean;
}

// ─── Data Types ──────────────────────────────────────────────────────────────

interface RecentLead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  status: string;
  created_at: string;
}

interface RecentClient {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  projectCount: number;
  status: string;
}

interface RecentDeal {
  id: string;
  name: string;
  value: number;
  status: string;
  stage_name: string;
  contact_name: string | null;
  created_at: string;
}

interface RecentTicket {
  id: string;
  ticket_number: number;
  subject: string;
  status: string;
  priority: string;
  created_at: string;
}

const priorityColor: Record<string, string> = {
  urgent: 'text-red-500 bg-red-500/10',
  high: 'text-orange-500 bg-orange-500/10',
  medium: 'text-yellow-500 bg-yellow-500/10',
  low: 'text-blue-500 bg-blue-500/10',
};

interface CrmStats {
  totalLeads: number;
  totalClients: number;
  pipelineValue: number;
  winRate: number;
  pendingInvoices: number;
  monthlyRevenue: number;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  title: string;
  count: number;
  linkTo: string;
  onNavigate: (path: string) => void;
  isGlobalDarkMode: boolean;
}> = ({ title, count, linkTo, onNavigate, isGlobalDarkMode }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <h2 className={cn('text-sm font-semibold', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>{title}</h2>
      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', isGlobalDarkMode ? 'bg-muted text-muted-foreground' : 'bg-gray-100 text-gray-500')}>
        {count}
      </span>
    </div>
    <button
      onClick={() => onNavigate(linkTo)}
      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
    >
      See all <ArrowRight className="w-3 h-3" />
    </button>
  </div>
);

const EmptyState: React.FC<{ label: string; isGlobalDarkMode: boolean }> = ({ label, isGlobalDarkMode }) => (
  <div className={cn('rounded-xl border border-dashed py-10 text-center', isGlobalDarkMode ? 'border-border' : 'border-gray-200')}>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const CrmStatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  percentage?: number;
  isGlobalDarkMode: boolean;
  onClick?: () => void;
}> = ({ title, value, icon, color, percentage, isGlobalDarkMode, onClick }) => {
  const isPositive = (percentage ?? 0) >= 0;
  return (
    <div
      className={cn("rounded-2xl border border-border bg-card transition-all p-5 flex flex-col gap-3", onClick && "cursor-pointer hover:shadow-md")}
      onClick={onClick}
    >
      <div className="flex justify-between items-center">
        <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", color)}>
          {icon}
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
      <div>
        <span className={cn("text-xs font-medium", isGlobalDarkMode ? "text-muted-foreground" : "text-gray-500")}>{title}</span>
        <div className="text-2xl font-bold tracking-tight text-foreground mt-0.5">
          {value}
        </div>
      </div>
      {percentage !== undefined && (
        <div className="flex items-center gap-2">
          <div className={cn("flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md",
            isPositive
              ? "text-emerald-600 bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-500"
              : "text-red-600 bg-red-100 dark:bg-red-500/10 dark:text-red-500"
          )}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(percentage)}%
          </div>
          <span className={cn("text-xs", isGlobalDarkMode ? "text-muted-foreground" : "text-gray-500")}>vs last period</span>
        </div>
      )}
    </div>
  );
};

const CrmHomeSkeleton = () => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5 h-[140px]">
          <Skeleton className="h-9 w-9 rounded-xl mb-3" />
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-7 w-28" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-4 w-6 rounded-full" />
            </div>
            <Skeleton className="h-3 w-12 rounded" />
          </div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="flex items-center gap-3 p-1">
                <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <Skeleton className="h-3 w-[70%] rounded" />
                  <Skeleton className="h-2 w-[40%] rounded" />
                </div>
                <Skeleton className="h-3 w-10 rounded shrink-0" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

// ─── Lead Status Badge ───────────────────────────────────────────────────────

const leadStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-100 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'New' },
  contacted: { bg: 'bg-amber-100 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'Contacted' },
  qualified: { bg: 'bg-emerald-100 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', label: 'Qualified' },
  proposal: { bg: 'bg-purple-100 dark:bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Proposal' },
  won: { bg: 'bg-green-100 dark:bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Won' },
  lost: { bg: 'bg-red-100 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Lost' },
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const CrmHome: React.FC<CrmHomeProps> = ({ isGlobalDarkMode }) => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { profile } = useAuth();
  const firstName = profile?.first_name || 'there';

  const [leads, setLeads] = useState<RecentLead[]>([]);
  const [clients, setClients] = useState<RecentClient[]>([]);
  const [deals, setDeals] = useState<RecentDeal[]>([]);
  const [tickets, setTickets] = useState<RecentTicket[]>([]);
  const [stats, setStats] = useState<CrmStats>({
    totalLeads: 0,
    totalClients: 0,
    pipelineValue: 0,
    winRate: 0,
    pendingInvoices: 0,
    monthlyRevenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentWorkspace?.id) return;

    // Cache check for instant paint
    const cachedLeads = sessionCache.get<RecentLead[]>(`crm_home_leads_${currentWorkspace.id}`);
    const cachedClients = sessionCache.get<RecentClient[]>(`crm_home_clients_${currentWorkspace.id}`);
    const cachedDeals = sessionCache.get<RecentDeal[]>(`crm_home_deals_${currentWorkspace.id}`);
    const cachedTickets = sessionCache.get<RecentTicket[]>(`crm_home_tickets_${currentWorkspace.id}`);
    const cachedStats = sessionCache.get<CrmStats>(`crm_home_stats_${currentWorkspace.id}`);

    if (cachedLeads) setLeads(cachedLeads);
    if (cachedClients) setClients(cachedClients);
    if (cachedDeals) setDeals(cachedDeals);
    if (cachedTickets) setTickets(cachedTickets);
    if (cachedStats) setStats(cachedStats);

    if (cachedLeads || cachedClients || cachedDeals || cachedTickets) {
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchAll = async () => {
      await Promise.allSettled([
        // Leads
        (async () => {
          const { data: leadsData } = await supabase
            .from('crm_leads')
            .select('id, first_name, last_name, email, phone, company_name, status, created_at')
            .eq('workspace_id', currentWorkspace.id)
            .order('created_at', { ascending: false })
            .limit(5);

          if (leadsData) {
            const formattedLeads = leadsData.map((l: any) => ({
              id: l.id,
              name: `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email,
              email: l.email,
              phone: l.phone,
              company: l.company_name,
              status: l.status,
              created_at: l.created_at
            }));
            setLeads(formattedLeads as RecentLead[]);
            sessionCache.set(`crm_home_leads_${currentWorkspace.id}`, formattedLeads);
          }
        })(),

        // Clients
        (async () => {
          try {
            const clientsData = await projectService.getWorkspaceClients(currentWorkspace.id);
            const recentClients = clientsData.slice(0, 5) as RecentClient[];
            setClients(recentClients);
            sessionCache.set(`crm_home_clients_${currentWorkspace.id}`, recentClients);
          } catch (err) {
            console.error('Error fetching clients in CRM Home:', err);
          }
        })(),

        // Pipeline Deals
        (async () => {
          const { data: dealsData } = await supabase
            .from('deals')
            .select('id, title, value, status, stage:stage_id(title), created_at')
            .eq('workspace_id', currentWorkspace.id)
            .not('status', 'in', '("won","lost")')
            .order('created_at', { ascending: false })
            .limit(5);

          if (dealsData) {
            const formattedDeals = dealsData.map((d: any) => ({
              id: d.id,
              name: d.title,
              value: d.value,
              status: d.status,
              stage_name: d.stage?.title || 'Pipeline',
              contact_name: null,
              created_at: d.created_at
            }));
            setDeals(formattedDeals as RecentDeal[]);
            sessionCache.set(`crm_home_deals_${currentWorkspace.id}`, formattedDeals);
          }
        })(),

        // Tickets
        (async () => {
          const { data: ticketsData } = await supabase
            .from('support_tickets')
            .select('id, ticket_number, subject, status, priority, created_at')
            .eq('workspace_id', currentWorkspace.id)
            .order('created_at', { ascending: false })
            .limit(5);

          if (ticketsData) {
            const recentTickets = ticketsData as RecentTicket[];
            setTickets(recentTickets);
            sessionCache.set(`crm_home_tickets_${currentWorkspace.id}`, recentTickets);
          }
        })(),

        // Stats
        (async () => {
          try {
            const [leadsResult, clientsData, dealsResult] = await Promise.all([
              supabase
                .from('crm_leads')
                .select('*', { count: 'exact', head: true })
                .eq('workspace_id', currentWorkspace.id),
              projectService.getWorkspaceClients(currentWorkspace.id),
              supabase
                .from('deals')
                .select('value, status')
                .eq('workspace_id', currentWorkspace.id),
            ]);

            let pipelineValue = 0;
            let wonDeals = 0;
            let totalClosedDeals = 0;

            if (dealsResult.data) {
              dealsResult.data.forEach((deal: any) => {
                if (deal.status !== 'lost') {
                  pipelineValue += (deal.value || 0);
                }
                if (deal.status === 'won') {
                  wonDeals++;
                  totalClosedDeals++;
                } else if (deal.status === 'lost') {
                  totalClosedDeals++;
                }
              });
            }

            const winRate = totalClosedDeals > 0 ? Math.round((wonDeals / totalClosedDeals) * 100) : 0;

            const newStats: CrmStats = {
              totalLeads: leadsResult.count || 0,
              totalClients: clientsData?.length || 0,
              pipelineValue,
              winRate,
              pendingInvoices: 0, // TODO: wire up when invoices table is ready
              monthlyRevenue: 0, // TODO: wire up
            };

            setStats(newStats);
            sessionCache.set(`crm_home_stats_${currentWorkspace.id}`, newStats);
          } catch (err) {
            console.error('Error fetching CRM stats:', err);
          }
        })(),
      ]);

      setLoading(false);
    };

    fetchAll();
  }, [currentWorkspace?.id]);

  const card = 'rounded-2xl border border-border bg-card transition-all p-5';
  const row = 'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border border-border hover:bg-muted/50';

  if (loading && !currentWorkspace) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className={cn("flex-1 min-h-screen flex flex-col", isGlobalDarkMode ? "bg-background" : "bg-white")}
      style={{
        '--background': isGlobalDarkMode ? '240 6% 10%' : '0 0% 100%',
        '--card': isGlobalDarkMode ? '240 5% 14%' : '0 0% 100%'
      } as React.CSSProperties}
    >
      <div className="w-full px-6 md:px-10 pt-4 pb-8">

        <motion.h1
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={cn('text-xl md:text-2xl font-bold tracking-tight mb-6', isGlobalDarkMode ? 'text-white' : 'text-gray-900')}
        >
          Welcome, {firstName}!
        </motion.h1>

        {loading ? (
          <CrmHomeSkeleton />
        ) : (
          <>
            {/* ── Stats Grid ─────────────────────────────────────────────── */}
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.0 }}
            >
              <CrmStatCard
                title="Total Clients"
                value={stats.totalClients}
                icon={<Users className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />}
                color="bg-emerald-100 dark:bg-emerald-500/10"
                percentage={8}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/crm/clients')}
              />
              <CrmStatCard
                title="Deals Value"
                value={`$${stats.pipelineValue.toLocaleString()}`}
                icon={<DollarSign className="w-4.5 h-4.5 text-purple-600 dark:text-purple-400" />}
                color="bg-purple-100 dark:bg-purple-500/10"
                percentage={5}
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/deals')}
              />
              <CrmStatCard
                title="Win Rate"
                value={`${stats.winRate}%`}
                icon={<Target className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />}
                color="bg-amber-100 dark:bg-amber-500/10"
                percentage={3}
                isGlobalDarkMode={isGlobalDarkMode}
              />
              <CrmStatCard
                title="Pending Invoices"
                value={stats.pendingInvoices}
                icon={<Receipt className="w-4.5 h-4.5 text-orange-600 dark:text-orange-400" />}
                color="bg-orange-100 dark:bg-orange-500/10"
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/crm/invoices')}
              />
              <CrmStatCard
                title="Monthly Revenue"
                value={`$${stats.monthlyRevenue.toLocaleString()}`}
                icon={<DollarSign className="w-4.5 h-4.5 text-teal-600 dark:text-teal-400" />}
                color="bg-teal-100 dark:bg-teal-500/10"
                isGlobalDarkMode={isGlobalDarkMode}
                onClick={() => navigate('/services/orders')}
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* ── Recent Leads ────────────────────────────────────────── */}
              <motion.div
                className={card}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <SectionHeader
                  title="Recent Leads"
                  count={leads.length}
                  linkTo="/crm/leads"
                  onNavigate={navigate}
                  isGlobalDarkMode={isGlobalDarkMode}
                />

                {leads.length === 0 ? (
                  <EmptyState label="No leads yet — add your first lead." isGlobalDarkMode={isGlobalDarkMode} />
                ) : (
                  <div className="space-y-1">
                    {leads.map((lead) => {
                      const statusInfo = leadStatusConfig[lead.status] || leadStatusConfig.new;
                      return (
                        <div key={lead.id} className={row} onClick={() => navigate('/crm/leads')}>
                          <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-muted border-border' : 'bg-gray-50 border-gray-100')}>
                            <UserPlus className="w-4 h-4 text-blue-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                              {lead.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {lead.company && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                  <Building2 className="w-3 h-3" />{lead.company}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', statusInfo.bg, statusInfo.text)}>
                            {statusInfo.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={() => navigate('/crm/leads')}
                  className={cn('mt-4 w-full py-2.5 rounded-lg text-xs font-semibold transition-colors border', isGlobalDarkMode ? 'bg-muted/50 border-border text-foreground hover:bg-muted' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <UserPlus className="w-3.5 h-3.5" /> Go to Leads
                  </span>
                </button>
              </motion.div>

              {/* ── Pipeline / Active Deals ─────────────────────────────── */}
              <motion.div
                className={card}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <SectionHeader
                  title="Active Deals"
                  count={deals.length}
                  linkTo="/deals"
                  onNavigate={navigate}
                  isGlobalDarkMode={isGlobalDarkMode}
                />

                {deals.length === 0 ? (
                  <EmptyState label="No active deals." isGlobalDarkMode={isGlobalDarkMode} />
                ) : (
                  <div className="space-y-1">
                    {deals.map((deal) => (
                      <div key={deal.id} className={row} onClick={() => navigate('/deals')}>
                        <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-muted border-border' : 'bg-gray-50 border-gray-100')}>
                          <Handshake className="w-4 h-4 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {deal.name}
                          </p>
                          <span className="text-xs text-muted-foreground">{deal.stage_name || 'Deal'}</span>
                        </div>
                        <span className={cn('text-xs font-bold', 'text-emerald-500')}>
                          ${deal.value?.toLocaleString() || '0'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => navigate('/deals')}
                  className={cn('mt-4 w-full py-2.5 rounded-lg text-xs font-semibold transition-colors border', isGlobalDarkMode ? 'bg-muted/50 border-border text-foreground hover:bg-muted' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Handshake className="w-3.5 h-3.5" /> Go to Deals
                  </span>
                </button>
              </motion.div>

              {/* ── Recent Clients ──────────────────────────────────────── */}
              <motion.div
                className={card}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <SectionHeader
                  title="Clients"
                  count={clients.length}
                  linkTo="/crm/clients"
                  onNavigate={navigate}
                  isGlobalDarkMode={isGlobalDarkMode}
                />

                {clients.length === 0 ? (
                  <EmptyState label="No clients yet — convert a lead." isGlobalDarkMode={isGlobalDarkMode} />
                ) : (
                  <div className="space-y-1">
                    {clients.map((client) => (
                      <div key={client.id} className={row} onClick={() => navigate('/crm/clients')}>
                        <div className={cn('w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden border', isGlobalDarkMode ? 'bg-muted border-border' : 'bg-gray-50 border-gray-100')}>
                          {client.avatar ? (
                            <img src={client.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {client.name}
                          </p>
                          <span className="text-xs text-muted-foreground truncate">{client.email}</span>
                        </div>
                        {client.projectCount > 0 && (
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            {client.projectCount} project{client.projectCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => navigate('/crm/clients')}
                  className={cn('mt-4 w-full py-2.5 rounded-lg text-xs font-semibold transition-colors border', isGlobalDarkMode ? 'bg-muted/50 border-border text-foreground hover:bg-muted' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Users className="w-3.5 h-3.5" /> Go to Clients
                  </span>
                </button>
              </motion.div>

              {/* ── Tickets ────────────────────────────────────────────── */}
              <motion.div
                className={card}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <SectionHeader
                  title="Tickets"
                  count={tickets.length}
                  linkTo="/tickets"
                  onNavigate={navigate}
                  isGlobalDarkMode={isGlobalDarkMode}
                />

                {tickets.length === 0 ? (
                  <EmptyState label="No open tickets — all caught up!" isGlobalDarkMode={isGlobalDarkMode} />
                ) : (
                  <div className="space-y-1">
                    {tickets.map((ticket) => (
                      <div key={ticket.id} className={row} onClick={() => navigate(`/tickets?id=${ticket.id}`)}>
                        <div className={cn('w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center border', isGlobalDarkMode ? 'bg-muted border-border' : 'bg-gray-50 border-gray-100')}>
                          <Ticket className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', isGlobalDarkMode ? 'text-foreground' : 'text-gray-800')}>
                            {ticket.subject}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">#{ticket.ticket_number}</span>
                            <span className="text-muted-foreground/40">·</span>
                            <span className={cn('text-[10px] capitalize font-medium',
                              ticket.status === 'open' ? 'text-blue-500' :
                              ticket.status === 'in_progress' ? 'text-amber-500' :
                              ticket.status === 'resolved' ? 'text-emerald-500' : 'text-muted-foreground'
                            )}>
                              {ticket.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <span className={cn('text-[10px] font-medium capitalize px-1.5 py-0.5 rounded', priorityColor[ticket.priority] || 'text-muted-foreground')}>
                          {ticket.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => navigate('/tickets')}
                  className={cn('mt-4 w-full py-2.5 rounded-lg text-xs font-semibold transition-colors border', isGlobalDarkMode ? 'bg-muted/50 border-border text-foreground hover:bg-muted' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Ticket className="w-3.5 h-3.5" /> Go to Tickets
                  </span>
                </button>
              </motion.div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
